import React, { useEffect, useRef, useState } from "react";
import { format, intervalToDuration } from "date-fns";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import { makeStyles } from "@material-ui/core/styles";
import { useRecording } from "../../util/context/RecordingContext";
import { MAX_RECORDING_MS, WARNING_THRESHOLD_MS } from "../../util/recordingSession";

const ContentCopyIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M9 18C8.45 18 7.97917 17.8042 7.5875 17.4125C7.19583 17.0208 7 16.55 7 16V4C7 3.45 7.19583 2.97917 7.5875 2.5875C7.97917 2.19583 8.45 2 9 2H18C18.55 2 19.0208 2.19583 19.4125 2.5875C19.8042 2.97917 20 3.45 20 4V16C20 16.55 19.8042 17.0208 19.4125 17.4125C19.0208 17.8042 18.55 18 18 18H9ZM9 16H18V4H9V16ZM5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V6H5V20H16V22H5Z"
      fill="#039EC3"
    />
  </svg>
);

const STORAGE_KEY = "recordingIndicator:position";

type Position = { x: number; y: number };

const readPosition = (): Position => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { x: 0, y: 0 };
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
      return parsed;
    }
  } catch {
    // ignore corrupt storage
  }
  return { x: 0, y: 0 };
};

const writePosition = (pos: Position) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore quota / disabled storage
  }
};

const fmt = (ms: number): string => {
  const { minutes = 0, seconds = 0 } = intervalToDuration({ start: 0, end: ms });
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const useStyles = makeStyles((theme) => ({
  "@keyframes recordingPulse": {
    "0%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.35, transform: "scale(0.85)" },
    "100%": { opacity: 1, transform: "scale(1)" },
  },
  "@keyframes slideDown": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  positioner: {
    position: "fixed",
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: theme.zIndex.snackbar + 1,
  },
  outerContainer: {
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 4px 8px 0 rgba(0, 69, 85, 0.16), 0 4px 12px 4px rgba(0, 69, 85, 0.08)",
    width: 451,
    transition: theme.transitions.create(["transform", "box-shadow"], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  outerContainerDragging: {
    transform: "scale(1.03)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
  },
  root: {
    display: "flex",
    width: "100%",
    minWidth: 80,
    padding: 8,
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#212936",
    color: "#FFFFFF",
    userSelect: "none",
  },
  previousRecordingsPanel: {
    backgroundColor: "#FFFFFF",
    padding: theme.spacing(2),
    animation: "$slideDown 200ms ease",
  },
  previousRecordingsTitle: {
    display: "flex",
    height: 16,
    flexDirection: "column",
    justifyContent: "center",
    whiteSpace: "nowrap",
    color: "#212936",
    textAlign: "left",
    fontFamily: "Figtree",
    fontSize: 13,
    fontWeight: 400,
    lineHeight: "120%",
    marginBottom: theme.spacing(1),
  },
  previousRecordingsEmpty: {
    color: "#212936",
    textAlign: "left",
    fontFamily: "Figtree",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "120%",
  },
  recordingItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  recordingItemLeft: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  recordingItemDate: {
    fontFamily: "Figtree",
    fontSize: 14,
    fontWeight: 600,
    color: "#212936",
    lineHeight: "120%",
  },
  recordingItemDuration: {
    fontFamily: "Figtree",
    fontSize: 13,
    fontWeight: 400,
    color: "#6B7280",
    lineHeight: "120%",
  },
  recordingCopyButton: {
    color: "#039EC3",
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "rgba(3, 158, 195, 0.08)",
    },
  },
  recordingsList: {
    maxHeight: 168,
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#C2C6CC",
      borderRadius: 3,
      filter: "drop-shadow(0 4px 12px rgba(0, 69, 85, 0.08)) drop-shadow(0 4px 8px rgba(0, 69, 85, 0.16))",
    },
  },
  rootRecording: {
    backgroundColor: "#C62828",
  },
  stopPauseControl: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: theme.spacing(0.5),
    height: theme.spacing(4),
    padding: theme.spacing(0.5),
    justifyContent: "center",
    gap: theme.spacing(1),
    overflow: "hidden",
  },
  controlButton: {
    padding: 6,
    borderRadius: 0,
    color: "#212936",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
  },
  controlIcon: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    aspectRatio: "1 / 1",
  },
  controlDivider: {
    width: 1,
    alignSelf: "stretch",
    backgroundColor: "rgba(0, 0, 0, 0.12)",
  },
  handle: {
    width: 20,
    alignSelf: "stretch",
    aspectRatio: "1 / 1",
    display: "inline-flex",
    alignItems: "center",
    cursor: "grab",
    "&:active": { cursor: "grabbing" },
  },
  label: {
    color: "#FFF",
    textAlign: "center",
    fontFamily: "Figtree",
    fontSize: 16,
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  leftGroup: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  rightGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  recordButton: {
    backgroundColor: "#C62828",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    fontFamily: "Figtree",
    fontStyle: "normal",
    textAlign: "center",
    fontSize: 16,
    padding: "4px 24px 4px 12px",
    minWidth: 100,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#A93226",
    },
  },
  recordDot: {
    fontSize: 10,
    color: "#FFFFFF",
    width: 16,
    height: 16,
    aspectRatio: "1 / 1",
    marginRight: theme.spacing(0.5),
  },
  recordingDot: {
    width: 16,
    height: 16,
    aspectRatio: "1 / 1",
    flexShrink: 0,
    animation: "$recordingPulse 1.4s infinite ease-in-out",
  },
  timer: {
    fontSize: 12,
    fontVariantNumeric: "tabular-nums",
    fontWeight: 500,
    color: "#FFFFFF",
    minWidth: 32,
  },
  warningHint: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    color: theme.palette.warning.light,
    whiteSpace: "nowrap",
  },
  warningIcon: {
    fontSize: 14,
  },
  closeIcon: {
    width: 14,
    height: 14,
    fill: "#FFF",
    flexShrink: 0,
  },
  chevronIcon: {
    width: 24,
    height: 24,
    aspectRatio: "1 / 1",
    flexShrink: 0,
    transition: "transform 200ms ease",
  },
  chevronCollapsed: {
    transform: "rotate(180deg)",
  },
  iconButton: {
    color: "rgba(255, 255, 255, 0.7)",
    padding: theme.spacing(0.5),
    "&:hover": {
      color: "#FFFFFF",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  modalIcon: {
    color: "#C0392B",
    fontSize: 48,
  },
}));

const RecordingIndicator = () => {
  const classes = useStyles();
  const {
    isRecording,
    isPaused,
    startedAt,
    stopAndShare,
    start,
    pause,
    resume,
    isIndicatorVisible,
    hideIndicator,
    previousRecordings,
  } = useRecording();
  const [now, setNow] = useState(Date.now());
  const [position, setPosition] = useState<Position>(() => readPosition());
  const [dragging, setDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const nodeRef = useRef<HTMLDivElement>(null);
  const totalPausedMsRef = useRef(0);
  const pausedAtRef = useRef<number | null>(null);

  // Reset pause tracking when recording ends.
  useEffect(() => {
    if (!isRecording) {
      totalPausedMsRef.current = 0;
      pausedAtRef.current = null;
    }
  }, [isRecording]);

  // Accumulate paused duration each time isPaused toggles.
  useEffect(() => {
    if (isPaused) {
      pausedAtRef.current = Date.now();
    } else if (pausedAtRef.current !== null) {
      totalPausedMsRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }
  }, [isPaused]);

  useEffect(() => {
    if (!isRecording || isPaused) {
      return;
    }
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isRecording, isPaused]);

  if (!isIndicatorVisible) {
    return null;
  }

  const elapsed = isRecording && startedAt ? now - startedAt.getTime() - totalPausedMsRef.current : 0;
  const near = elapsed >= WARNING_THRESHOLD_MS;
  const remaining = Math.max(0, MAX_RECORDING_MS - elapsed);

  const handleDragStop = (_e: DraggableEvent, data: DraggableData) => {
    setDragging(false);
    const next = { x: data.x, y: data.y };
    setPosition(next);
    writePosition(next);
  };

  return (
    <>
      <Draggable
        nodeRef={nodeRef}
        handle={`.${classes.handle}`}
        bounds="body"
        position={position}
        onStart={() => setDragging(true)}
        onStop={handleDragStop}
      >
        <div ref={nodeRef} className={classes.positioner}>
          <div className={`${classes.outerContainer} ${dragging ? classes.outerContainerDragging : ""}`}>
            <div
              className={`${classes.root} ${isRecording ? classes.rootRecording : ""}`}
              role="status"
              aria-live="polite"
            >
              {/* Left group: drag handle + content */}
              <div className={classes.leftGroup}>
                <div className={classes.handle} aria-label="Drag recording indicator">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M7.50016 16.6667C7.04183 16.6667 6.64961 16.5037 6.3235 16.1775C5.99683 15.8509 5.8335 15.4584 5.8335 15C5.8335 14.5417 5.99683 14.1492 6.3235 13.8225C6.64961 13.4964 7.04183 13.3334 7.50016 13.3334C7.9585 13.3334 8.351 13.4964 8.67766 13.8225C9.00377 14.1492 9.16683 14.5417 9.16683 15C9.16683 15.4584 9.00377 15.8509 8.67766 16.1775C8.351 16.5037 7.9585 16.6667 7.50016 16.6667ZM12.5002 16.6667C12.0418 16.6667 11.6496 16.5037 11.3235 16.1775C10.9968 15.8509 10.8335 15.4584 10.8335 15C10.8335 14.5417 10.9968 14.1492 11.3235 13.8225C11.6496 13.4964 12.0418 13.3334 12.5002 13.3334C12.9585 13.3334 13.351 13.4964 13.6777 13.8225C14.0038 14.1492 14.1668 14.5417 14.1668 15C14.1668 15.4584 14.0038 15.8509 13.6777 16.1775C13.351 16.5037 12.9585 16.6667 12.5002 16.6667ZM7.50016 11.6667C7.04183 11.6667 6.64961 11.5034 6.3235 11.1767C5.99683 10.8506 5.8335 10.4584 5.8335 10C5.8335 9.54171 5.99683 9.14921 6.3235 8.82254C6.64961 8.49643 7.04183 8.33337 7.50016 8.33337C7.9585 8.33337 8.351 8.49643 8.67766 8.82254C9.00377 9.14921 9.16683 9.54171 9.16683 10C9.16683 10.4584 9.00377 10.8506 8.67766 11.1767C8.351 11.5034 7.9585 11.6667 7.50016 11.6667ZM12.5002 11.6667C12.0418 11.6667 11.6496 11.5034 11.3235 11.1767C10.9968 10.8506 10.8335 10.4584 10.8335 10C10.8335 9.54171 10.9968 9.14921 11.3235 8.82254C11.6496 8.49643 12.0418 8.33337 12.5002 8.33337C12.9585 8.33337 13.351 8.49643 13.6777 8.82254C14.0038 9.14921 14.1668 9.54171 14.1668 10C14.1668 10.4584 14.0038 10.8506 13.6777 11.1767C13.351 11.5034 12.9585 11.6667 12.5002 11.6667ZM7.50016 6.66671C7.04183 6.66671 6.64961 6.50337 6.3235 6.17671C5.99683 5.8506 5.8335 5.45837 5.8335 5.00004C5.8335 4.54171 5.99683 4.14948 6.3235 3.82337C6.64961 3.49671 7.04183 3.33337 7.50016 3.33337C7.9585 3.33337 8.351 3.49671 8.67766 3.82337C9.00377 4.14948 9.16683 4.54171 9.16683 5.00004C9.16683 5.45837 9.00377 5.8506 8.67766 6.17671C8.351 6.50337 7.9585 6.66671 7.50016 6.66671ZM12.5002 6.66671C12.0418 6.66671 11.6496 6.50337 11.3235 6.17671C10.9968 5.8506 10.8335 5.45837 10.8335 5.00004C10.8335 4.54171 10.9968 4.14948 11.3235 3.82337C11.6496 3.49671 12.0418 3.33337 12.5002 3.33337C12.9585 3.33337 13.351 3.49671 13.6777 3.82337C14.0038 4.14948 14.1668 4.54171 14.1668 5.00004C14.1668 5.45837 14.0038 5.8506 13.6777 6.17671C13.351 6.50337 12.9585 6.66671 12.5002 6.66671Z"
                      fill="white"
                    />
                  </svg>
                </div>

                {isRecording && startedAt && (
                  <>
                    {isPaused ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        style={{ width: 16, height: 16, aspectRatio: "1/1", flexShrink: 0 }}
                      >
                        <rect x="9.33325" y="3.33333" width="2.66667" height="9.33333" fill="white" />
                        <rect x="4" y="3.33333" width="2.66667" height="9.33333" fill="white" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className={classes.recordingDot}
                      >
                        <circle cx="8" cy="8" r="4" fill="white" />
                      </svg>
                    )}
                    <span className={classes.label}>{isPaused ? "Paused" : "Recording..."}</span>
                    {near && (
                      <span className={classes.warningHint}>
                        <WarningRoundedIcon className={classes.warningIcon} />
                        stops in {fmt(remaining)}
                      </span>
                    )}
                  </>
                )}

                {!isRecording && <span className={classes.label}>Record a session</span>}
              </div>
              {/* Right group: record button + chevron + close (pre-recording)
                or standalone chevron (recording) */}
              {isRecording ? (
                <div className={classes.rightGroup}>
                  {startedAt && <span className={classes.timer}>{fmt(elapsed)}</span>}
                  <div className={classes.stopPauseControl}>
                    <IconButton
                      className={classes.controlButton}
                      size="small"
                      onClick={stopAndShare}
                      aria-label="Stop recording"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="none"
                        className={classes.controlIcon}
                      >
                        <rect x="3" y="3" width="10" height="10" rx="1" fill="#212936" />
                      </svg>
                    </IconButton>
                    <div className={classes.controlDivider} />
                    <IconButton
                      className={classes.controlButton}
                      size="small"
                      onClick={isPaused ? resume : pause}
                      aria-label={isPaused ? "Resume recording" : "Pause recording"}
                    >
                      {isPaused ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          style={{ width: 24, height: 24, aspectRatio: "1/1", flexShrink: 0 }}
                        >
                          <circle cx="12" cy="12" r="6" fill="#C62828" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          fill="none"
                          className={classes.controlIcon}
                        >
                          <rect x="3" y="2" width="3.5" height="12" rx="1" fill="#212936" />
                          <rect x="9.5" y="2" width="3.5" height="12" rx="1" fill="#212936" />
                        </svg>
                      )}
                    </IconButton>
                  </div>
                  <IconButton
                    className={classes.iconButton}
                    size="small"
                    onClick={() => setCollapsed((c) => !c)}
                    aria-label={collapsed ? "Expand" : "Collapse"}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className={`${classes.chevronIcon} ${collapsed ? classes.chevronCollapsed : ""}`}
                    >
                      <path d="M12 8L6 14L7.41 15.41L12 10.83L16.59 15.41L18 14L12 8Z" fill="white" />
                    </svg>
                  </IconButton>
                  <IconButton className={classes.iconButton} size="small" onClick={hideIndicator} aria-label="Dismiss">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      className={classes.closeIcon}
                    >
                      <path
                        d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
                        fill="white"
                      />
                    </svg>
                  </IconButton>
                </div>
              ) : (
                <div className={classes.rightGroup}>
                  <Button className={classes.recordButton} size="small" onClick={start} disableElevation disableRipple>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="4" fill="white" />
                    </svg>
                    Record
                  </Button>
                  <IconButton
                    className={classes.iconButton}
                    size="small"
                    onClick={() => setCollapsed((c) => !c)}
                    aria-label={collapsed ? "Expand" : "Collapse"}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className={`${classes.chevronIcon} ${collapsed ? classes.chevronCollapsed : ""}`}
                    >
                      <path d="M12 8L6 14L7.41 15.41L12 10.83L16.59 15.41L18 14L12 8Z" fill="white" />
                    </svg>
                  </IconButton>
                  <IconButton className={classes.iconButton} size="small" onClick={hideIndicator} aria-label="Dismiss">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      className={classes.closeIcon}
                    >
                      <path
                        d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
                        fill="white"
                      />
                    </svg>
                  </IconButton>
                </div>
              )}
            </div>
            {!collapsed && (
              <div className={classes.previousRecordingsPanel}>
                <div className={classes.previousRecordingsTitle}>Previous Recordings</div>
                {previousRecordings.length === 0 ? (
                  <div className={classes.previousRecordingsEmpty}>No previous recordings to display</div>
                ) : (
                  <div className={classes.recordingsList}>
                    {previousRecordings.map((entry) => (
                      <div key={entry.timestamp} className={classes.recordingItem}>
                        <div className={classes.recordingItemLeft}>
                          <div className={classes.recordingItemDate}>
                            {format(new Date(entry.timestamp), "MMM dd, yyyy | hh:mm a")}
                          </div>
                          <div className={classes.recordingItemDuration}>{fmt(entry.durationMs)}</div>
                        </div>
                        <IconButton
                          className={classes.recordingCopyButton}
                          size="small"
                          onClick={() => navigator.clipboard.writeText(entry.url).catch(() => {})}
                          aria-label="Copy recording link"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Draggable>
    </>
  );
};

export default RecordingIndicator;
